import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';

import {
  CreateSurveyDto,
  CreateSurveyDtoAnonymizationTypeEnum,
  CreateSurveyDtoPriorityEnum,
  SurveyFieldDto,
  CreateSurveyDtoSendStrategyEnum,
  Organization,
  SurveyCreationDraftTableDto,
  SurveyCreationDraftDto,
  SurveyCreationDraftDtoPriorityEnum,
  SurveyCreationDraftDtoAnonymizationTypeEnum,
  SurveyCreationDraftDtoSendStrategyEnum,
} from 'apps/marsinet/src/app/typescript-axios';

import { FormField } from '../../../dashboards/components/form-field-add-dashboard/form-field';
import { ModalAddSurveyProps } from 'apps/marsinet/src/app/models/surveysModels';
import { useAddSurveysValidationSchema } from 'apps/marsinet/src/app/validations/addSurveysValidationSchema';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import OrganizationMultiselect from '../organization-multiselect/organization-multiselect';
import ModalQuestions from './modal-questions/modal-questions';
import QuestionsTable from './questions-table/questions-table';
import { useContext } from 'react';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import {
  getLocalDateTimeInTimeZone,
  localDateTimeToUtc,
} from 'apps/marsinet/src/app/utils/timezone-utils';

const ModalAddSurveys: React.FC<ModalAddSurveyProps & {
  draft?: SurveyCreationDraftTableDto | null;
  onDraftSaved: () => void;
}> = ({ visible, setVisible, createSurveyFunction, draft, onDraftSaved }) => {
  const { t } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<CreateSurveyDto | null>(
    null
  );
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const toastRef = useRef<Toast>(null);

  const [draftId, setDraftId] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [exitConfirmVisible, setExitConfirmVisible] = useState(false);
  const [draftConfirmVisible, setDraftConfirmVisible] = useState(false);


  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response =
          await apiServices.userApi.usersControllerGetOrganizations();
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('Error loading organizations', error);
        setOrganizations([]);
      }
    };

    if (visible) {
      loadOrganizations();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setDraftId(null);
      setExitConfirmVisible(false);
      return;
    }
    if (!draft) return;

    const loadDraft = async () => {
      try {
        const response =
          await apiServices.surveyApi.surveyControllerGetSurveyCreationDraft(draft.id);
        const d = response.data;
        setDraftId(draft.id);
        formik.resetForm({
          values: {
            name: d.name ?? '',
            description: d.description ?? '',
            study: d.study ?? '',
            template: (d.template as SurveyFieldDto[]) ?? [],
            priority: d.priority ?? CreateSurveyDtoPriorityEnum.Mandatory,
            send_frequency_days: d.send_frequency_days,
            response_time_limit_days: d.response_time_limit_days,
            starts_at: d.starts_at ?? '',
            anonymization_type: d.anonymization_type ?? CreateSurveyDtoAnonymizationTypeEnum.None,
            send_strategy: d.send_strategy ?? CreateSurveyDtoSendStrategyEnum.AdminOnly,
            organization_ids: [] as string[],
          } as CreateSurveyDto,
        });
      } catch (error) {
        toastRef.current?.show({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.surveys.draft_load_error'),
        });
      }
    };
    loadDraft();
  }, [visible, draft]);

  const saveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    const v = formik.values;
    const body: SurveyCreationDraftDto = {
      name: v.name || undefined,
      description: v.description || undefined,
      study: v.study || undefined,
      template: v.template,
      send_frequency_days: v.send_frequency_days,
      response_time_limit_days: v.response_time_limit_days,
      starts_at: v.starts_at || undefined,
      priority: v.priority as unknown as SurveyCreationDraftDtoPriorityEnum,
      anonymization_type: v.anonymization_type as unknown as SurveyCreationDraftDtoAnonymizationTypeEnum,
      send_strategy: v.send_strategy as unknown as SurveyCreationDraftDtoSendStrategyEnum,
    };
    try {
      if (draftId) {
        await apiServices.surveyApi.surveyControllerSaveSurveyCreationDraft(body, draftId);
      } else {
        const res = await apiServices.surveyApi.surveyControllerSaveNewSurveyCreationDraft(body);
        setDraftId(res.data.id);
      }
      onDraftSaved();
      formik.resetForm();
      setExitConfirmVisible(false);
      setVisible(false);
    } catch (error) {
      // keep the modal open so nothing typed is lost
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.surveys.draft_saved_error'),
      });
    } finally {
      setSavingDraft(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      study: '',
      template: [] as SurveyFieldDto[],
      priority: CreateSurveyDtoPriorityEnum.Mandatory,
      send_frequency_days: undefined,
      response_time_limit_days: undefined,
      starts_at: '',
      anonymization_type: CreateSurveyDtoAnonymizationTypeEnum.None,
      send_strategy: CreateSurveyDtoSendStrategyEnum.AdminOnly,
      organization_ids: [] as string[],
    } as CreateSurveyDto,
    validationSchema: useAddSurveysValidationSchema(),
    onSubmit: (values) => {
      setPendingValues(values);
      setShowConfirmDialog(true);
    },
  });

  const onPageHide = () => {
    formik.resetForm();
    setVisible(false);
  };

  const handleConfirmSave = () => {
    if (pendingValues) {
      createSurveyFunction(pendingValues, draftId ?? undefined);
      formik.resetForm();
      setShowConfirmDialog(false);
      setPendingValues(null);
      setVisible(false);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
    setPendingValues(null);
  };

  const getFieldsDependingOn = (fieldName: string) => {
    return formik.values.template.filter(
      (field) => field.condition?.field_name === fieldName
    );
  };

  const handleRemoveQuestion = (rowIndex: number) => {
    const fieldToRemove = formik.values.template[rowIndex];
    const dependentFields = getFieldsDependingOn(fieldToRemove.name);

    if (dependentFields.length > 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('pages.surveys.warning'),
        detail: t('pages.surveys.cannot_delete_field_has_dependencies'),
        life: 3000,
      });
      return;
    }

    const updatedTemplate = formik.values.template.filter(
      (_, i) => i !== rowIndex
    );
    formik.setFieldValue('template', updatedTemplate);
  };

  const isFormFieldInvalid = (name: string) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: string) =>
    isFormFieldInvalid(name) ? formik.errors[name] : null;

  const canCreate = () => {
    const valuesChanged =
      JSON.stringify(formik.initialValues) !== JSON.stringify(formik.values);
    return valuesChanged && formik.isValid && formik.values.template.length > 0;
  };

  const requestClose = () => {
    if (formik.dirty) {
      setExitConfirmVisible(true);
    } else {
      formik.resetForm();
      setVisible(false);
    }
  };

  const requestSaveDraft = () => {
    const hasOrgs =
      Array.isArray(formik.values.organization_ids) &&
      formik.values.organization_ids.length > 0;
    if (hasOrgs) {
      setDraftConfirmVisible(true);
    } else {
      saveDraft();
    }
  };

  const dialogHeader = (
    <div className="flex flex-wrap align-items-center justify-content-between w-full gap-2">
      <span className="text-xl font-bold">{t('pages.surveys.add')}</span>
      <div className="flex align-items-center gap-2">
        <Button
          id="button-create-survey"
          type="button"
          severity="secondary"
          icon="pi pi-save"
          label={t('pages.surveys.add')}
          className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
          onClick={() => formik.submitForm()}
          disabled={!canCreate()}
        />
        <Button
          type="button"
          icon="pi pi-save"
          label={t('pages.surveys.saveDraft')}
          className="p-button-outlined p-button-sm"
          onClick={requestSaveDraft}
          disabled={!formik.dirty}
          loading={savingDraft}
        />
        <Button
          type="button"
          icon="pi pi-sign-out"
          label={t('pages.surveys.exitWithoutSaving')}
          className="p-button-danger p-button-sm"
          onClick={requestClose}
        />
      </div>
    </div>
  );


  const discardAndExit = () => {
    setExitConfirmVisible(false);
    formik.resetForm();
    setVisible(false);
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header={dialogHeader}
        visible={visible}
        className="mx-2 md:w-12 lg:w-8"
        onHide={onPageHide}
        closable={false}
        closeOnEscape={false}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <form autoComplete="off" onSubmit={formik.handleSubmit}>

          {/* ── Section 1: General ───────────────────────────── */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3 pb-2 border-bottom-2 border-primary flex align-items-center gap-2">
              {t('pages.surveys.section_general')}
            </h3>
            <div className="grid formgrid p-fluid">
              {/* Name Field */}
              <FormField
                id="input-create-survey-name"
                label="pages.surveys.name"
                name="name"
                value={formik.values.name}
                placeholder="pages.surveys.name"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('name', e.target.value)}
                isInvalid={isFormFieldInvalid('name')}
                errorMessage={getFormErrorMessage('name')}
                className="col-12"
                height="40px"
                required
                maxLength={50}
              />

              {/* Description Field */}
              <div className="field mb-5 col-12">
                <div className="flex align-items-center justify-content-between mb-2">
                  <label className="font-bold block">
                    {t('pages.surveys.description')}
                  </label>
                  <small className="text-color-secondary">
                    {formik.values.description.length}/350
                  </small>
                </div>
                <InputTextarea
                  id="input-create-survey-description"
                  name="description"
                  value={formik.values.description}
                  onChange={(e) =>
                    formik.setFieldValue('description', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  rows={3}
                  maxLength={350}
                />
                {isFormFieldInvalid('description') && (
                  <small className="p-error">
                    *{getFormErrorMessage('description')}
                  </small>
                )}
              </div>

              {/* Study Field */}
              <FormField
                id="input-create-survey-study"
                label="pages.surveys.study"
                name="study"
                value={formik.values.study}
                placeholder="pages.surveys.study"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('study', e.target.value)}
                isInvalid={isFormFieldInvalid('study')}
                errorMessage={getFormErrorMessage('study')}
                className="col-12"
                height="40px"
              />
            </div>
          </div>

          {/* ── Section 2: Survey type & anonymization ───────── */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3 pb-2 border-bottom-2 border-primary flex align-items-center gap-2">
              {t('pages.surveys.section_configuration')}
            </h3>
            <div className="grid formgrid p-fluid">
              {/* Priority Field */}
              <div className="field mb-5 col-12 md:col-6">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.priority')}
                </label>
                <Dropdown
                  id="input-create-survey-priority"
                  name="priority"
                  value={formik.values.priority}
                  onChange={(e) =>
                    formik.setFieldValue('priority', e.target.value)
                  }
                  options={[
                    {
                      label: t('pages.surveys.priority_values.mandatory'),
                      value: CreateSurveyDtoPriorityEnum.Mandatory,
                    },
                    {
                      label: t('pages.surveys.priority_values.optional'),
                      value: CreateSurveyDtoPriorityEnum.Optional,
                    },
                  ]}
                  placeholder={t('select')}
                />
                {isFormFieldInvalid('priority') && (
                  <small className="p-error">
                    *{getFormErrorMessage('priority')}
                  </small>
                )}
              </div>

              {/* Anonymization Type */}
              <div className="field mb-5 col-12 md:col-6">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.anonymization_type')}
                </label>
                <Dropdown
                  id="input-create-survey-anonymization"
                  name="anonymization_type"
                  value={formik.values.anonymization_type}
                  onChange={(e) =>
                    formik.setFieldValue('anonymization_type', e.target.value)
                  }
                  options={[
                    {
                      label: t('pages.surveys.anonymization_type_values.none'),
                      value: CreateSurveyDtoAnonymizationTypeEnum.None,
                    },
                    {
                      label: t(
                        'pages.surveys.anonymization_type_values.all_answers'
                      ),
                      value: CreateSurveyDtoAnonymizationTypeEnum.AllAnswers,
                    },
                    {
                      label:
                        t('pages.surveys.anonymization_type_values.optional') +
                        ' ' +
                        t('pages.surveys.anonymization_optional_helper'),
                      value: CreateSurveyDtoAnonymizationTypeEnum.Optional,
                    },
                  ]}
                  placeholder={t('select')}
                />
                {isFormFieldInvalid('anonymization_type') && (
                  <small className="p-error">
                    *{getFormErrorMessage('anonymization_type')}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 3: Scheduling ────────────────────────── */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3 pb-2 border-bottom-2 border-primary flex align-items-center gap-2">
              {t('pages.surveys.section_scheduling')}
            </h3>
            <div className="grid formgrid p-fluid">
              {/* Start Date */}
              <div className="field mb-5 col-12 md:col-4">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.starts_at')}{' '}
                  <span className="p-error">*</span>
                </label>
                <Calendar
                  id="input-create-survey-start-date"
                  name="starts_at"
                  value={
                    formik.values.starts_at
                        ? getLocalDateTimeInTimeZone(
                            formik.values.starts_at,
                            selectedTimezone
                          )
                      : null
                  }
                  onChange={(e) => {
                    try {
                      // Mantiene los valores en UTC aunque el selector edite la hora de la zona elegida.
                      formik.setFieldValue(
                        'starts_at',
                        e.target.value
                          ? localDateTimeToUtc(
                              e.target.value,
                              selectedTimezone
                            )
                          : ''
                      );
                    } catch (error) {
                      if (error instanceof RangeError) {
                        formik.setFieldError('starts_at', error.message);
                        return;
                      }
                      throw error;
                    }
                  }}
                  onBlur={formik.handleBlur}
                  showTime
                  dateFormat="yy-mm-dd"
                />
                {isFormFieldInvalid('starts_at') && (
                  <small className="p-error">
                    *{getFormErrorMessage('starts_at')}
                  </small>
                )}
              </div>

              {/* Send Frequency Days */}
              <div className="field mb-5 col-12 md:col-4">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.send_frequency_days')}
                </label>
                <InputNumber
                  id="input-create-survey-frequency"
                  name="send_frequency_days"
                  value={formik.values.send_frequency_days}
                  onChange={(e: any) =>
                    formik.setFieldValue('send_frequency_days', e.value)
                  }
                  onBlur={formik.handleBlur}
                  min={1}
                  useGrouping={false}
                />
                {isFormFieldInvalid('send_frequency_days') && (
                  <small className="p-error">
                    *{getFormErrorMessage('send_frequency_days')}
                  </small>
                )}
              </div>

              {/* Response Time Limit Days */}
              <div className="field mb-5 col-12 md:col-4">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.response_time_limit_days')}
                </label>
                <InputNumber
                  id="input-create-survey-response-time"
                  name="response_time_limit_days"
                  value={formik.values.response_time_limit_days}
                  onChange={(e: any) =>
                    formik.setFieldValue('response_time_limit_days', e.value)
                  }
                  onBlur={formik.handleBlur}
                  min={1}
                  useGrouping={false}
                />
                {isFormFieldInvalid('response_time_limit_days') && (
                  <small className="p-error">
                    *{getFormErrorMessage('response_time_limit_days')}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 4: Recipients ────────────────────────── */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3 pb-2 border-bottom-2 border-primary flex align-items-center gap-2">
              {t('pages.surveys.section_recipients')}
            </h3>
            <div className="grid formgrid p-fluid">
              {/* Organizations */}
              <div className="field mb-5 col-12 md:col-6">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.organization_ids')}
                </label>
                <OrganizationMultiselect
                  organizations={organizations}
                  value={formik.values.organization_ids}
                  onChange={(selectedIds) =>
                    formik.setFieldValue('organization_ids', selectedIds)
                  }
                  placeholder={t('pages.surveys.select_organizations')}
                  maxSelectedLabels={3}
                  className="w-full md:w-100rem"
                />

                {Array.isArray(formik.values.organization_ids) &&
                  formik.values.organization_ids.length > 0 && (
                    <small className="block mt-2 text-color">
                      <strong>
                        {t('pages.surveys.selected_organizations')}:
                      </strong>{' '}
                      {formik.values.organization_ids
                        ?.map(
                          (id) =>
                            organizations.find((org) => org.o_id === id)?.name
                        )
                        .filter(Boolean)
                        .join(', ')}
                    </small>
                  )}
                {isFormFieldInvalid('organization_ids') && (
                  <small className="p-error">
                    *{getFormErrorMessage('organization_ids')}
                  </small>
                )}
              </div>

              {/* Send Strategy */}
              <div className="field mb-5 col-12 md:col-6">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.send_strategy')}
                </label>
                <Dropdown
                  id="input-create-survey-send-strategy"
                  name="send_strategy"
                  value={formik.values.send_strategy}
                  onChange={(e) =>
                    formik.setFieldValue('send_strategy', e.target.value)
                  }
                  options={[
                    {
                      label: t('pages.surveys.send_strategy_values.all_users'),
                      value: CreateSurveyDtoSendStrategyEnum.AllUsers,
                    },
                    {
                      label: t('pages.surveys.send_strategy_values.admin_only'),
                      value: CreateSurveyDtoSendStrategyEnum.AdminOnly,
                    },
                  ]}
                  placeholder={t('pages.surveys.select_strategy')}
                />
                {isFormFieldInvalid('send_strategy') && (
                  <small className="p-error">
                    *{getFormErrorMessage('send_strategy')}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 5: Questions ─────────────────────────── */}
          <div className="mb-4">
            <div className="flex align-items-center justify-content-between gap-3 mb-3">
              <h3 className="text-lg font-semibold m-0 pb-2 border-bottom-2 border-primary flex-grow-1 flex align-items-center gap-2">
                {t('pages.surveys.survey_questions')}{' '}
                <span className="p-error">*</span>
              </h3>
              <Button
                type="button"
                label={t('pages.surveys.add_questions')}
                icon="pi pi-plus"
                onClick={() => setShowQuestionsModal(true)}
                className="p-button-outlined p-button-sm flex-shrink-0"
              />
            </div>
            <div className="grid formgrid p-fluid">
              <div className="col-12">
                {formik.values.template.length > 0 && (
                  <small className="block mb-2 text-color-secondary">
                    {t('pages.surveys.questions_added', {
                      count: formik.values.template.length,
                    })}
                  </small>
                )}
                {formik.values.template.length > 0 && (
                  <div className="mb-3 mt-3">
                    <QuestionsTable
                      template={formik.values.template}
                      onTemplateChange={(template) =>
                        formik.setFieldValue('template', template)
                      }
                    />
                  </div>
                )}
                {formik.values.template.length === 0 && (
                  <div className="flex align-items-center justify-content-start mt-1 mx-2">
                    <small className="p-error">
                      *{t('pages.surveys.template_required')}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Dialog
            header={t('pages.surveys.add')}
            visible={showConfirmDialog}
            onHide={handleCancelSave}
            modal
            className="mx-2 md:w-25rem"
            footer={
              <div className="flex gap-2 justify-content-end">
                <Button
                  label={t('no')}
                  icon="pi pi-times"
                  onClick={handleCancelSave}
                  className="p-button-outlined"
                />
                <Button
                  label={t('yes')}
                  icon="pi pi-check"
                  onClick={handleConfirmSave}
                />
              </div>
            }
          >
            <div className="flex flex-column gap-3">
              <p>{t('pages.surveys.confirm_save_message')}</p>
            </div>
          </Dialog>
          <Dialog
            header={t('pages.surveys.exitConfirmHeader')}
            visible={exitConfirmVisible}
            onHide={() => setExitConfirmVisible(false)}
            modal
            className="mx-2 md:w-30rem"
            footer={
              <div className="flex gap-2 justify-content-end flex-wrap">
                <Button label={t('cancel')} className="p-button-outlined"
                  onClick={() => setExitConfirmVisible(false)} />
                <Button label={t('pages.surveys.exitWithoutSaving')}
                  className="p-button-danger p-button-outlined" onClick={discardAndExit} />
              </div>
            }
          >
            <p className="mt-0">{t('pages.surveys.exitConfirmMessage')}</p>
          </Dialog>
          <Dialog
            header={t('pages.surveys.saveDraftConfirmHeader')}
            visible={draftConfirmVisible}
            onHide={() => setDraftConfirmVisible(false)}
            modal
            className="mx-2 md:w-30rem"
            footer={
              <div className="flex gap-2 justify-content-end flex-wrap">
                <Button
                  label={t('cancel')}
                  className="p-button-outlined"
                  onClick={() => setDraftConfirmVisible(false)}
                />
                <Button
                  label={t('pages.surveys.saveDraft')}
                  icon="pi pi-save"
                  loading={savingDraft}
                  onClick={() => {
                    setDraftConfirmVisible(false);
                    saveDraft();
                  }}
                />
              </div>
            }
          >
            <p className="mt-0">{t('pages.surveys.saveDraftConfirmMessage')}</p>
          </Dialog>
        </form>
        <ModalQuestions
          visible={showQuestionsModal}
          onHide={() => setShowQuestionsModal(false)}
          template={formik.values.template}
          onTemplateChange={(template) =>
            formik.setFieldValue('template', template)
          }
        />
      </Dialog>
    </>
  );
};

export default ModalAddSurveys;
